import { P, type Permission } from '@/lib/permissions/catalog'

/**
 * Which live counter appears as a badge.
 *
 * Deliberately short: a key belongs here only once an endpoint returns the
 * number cheaply. Live visitors and open knowledge gaps are missing because
 * counting them today means downloading the whole list — see useNavBadges.
 */
export type BadgeKey = 'unread' | 'hotLeads' | 'tasksDue' | 'supportUnread'

/** Icon-chip colour. Every value maps to a contrast-verified token pair. */
export type NavTone =
  | 'action'
  | 'info'
  | 'success'
  | 'warning'
  | 'danger'
  | 'cold'
  | 'warm'
  | 'qualified'
  | 'hot'

/**
 * A sub-destination of a nav item.
 *
 * `to` is a complete destination relative to the workspace, search string
 * included: '/inbox?view=unread' really opens the inbox filtered to unread,
 * because every page's tab lives in the URL (see lib/router/useTabParam). A
 * child with no search string is the parent's default view.
 */
export interface NavChild {
  label: string
  to: string
  permission?: Permission
  tone?: NavTone
}

export interface NavItem {
  label: string
  /** Relative to /w/:slug. '' is the dashboard. */
  path: string
  /** Hidden entirely when the user lacks this. Never shown disabled. */
  permission?: Permission
  badge?: BadgeKey
  tone?: NavTone
  children?: NavChild[]
  /**
   * The "+ New …" row under an expanded group. `flag` is a search param the
   * page reads to open its create dialog, so the row is a real link and not a
   * button that only works from one screen.
   */
  quickAction?: { label: string; flag: string; permission?: Permission }
}

export interface NavSection {
  label?: string
  items: NavItem[]
  /** Hidden unless the workspace actually uses this area. */
  requiresCommerce?: boolean
  /** Group-dot colour, same token pairs as items. */
  tone?: NavTone
}

/**
 * Sidebar structure, grouped by how often each item is opened — not
 * alphabetically.
 *
 * Two rules hold this together:
 *
 * COMPLETE. Every route in App.tsx is reachable from here. A screen the router
 * serves but the navigation never mentions is a screen nobody finds.
 *
 * REAL. Every child is a genuine URL, not a label that scrolls somewhere. The
 * pages keep their tab in a search param precisely so this list can exist.
 *
 * Sections a workspace does not use are HIDDEN, not disabled: a Commerce
 * section with nothing in it is noise for a B2B consultancy.
 *
 * Reference: docs/04-frontend/04-routing-and-navigation.md
 */
export const NAVIGATION: NavSection[] = [
  {
    label: 'Workspace',
    tone: 'action',
    items: [
      { label: 'Dashboard', path: '', tone: 'action' },
      // The agent's home: one screen answering "what do I do now?". Above the
      // inbox because it includes the inbox's own urgent items.
      { label: 'My Day', path: '/my-day', badge: 'tasksDue', tone: 'warm' },
      {
        label: 'Conversations',
        path: '/inbox',
        permission: P.conversationsView,
        badge: 'unread',
        tone: 'info',
        children: [
          { label: 'All', to: '/inbox', tone: 'info' },
          { label: 'Unread', to: '/inbox?view=unread', tone: 'action' },
          { label: 'Waiting', to: '/inbox?view=waiting', tone: 'warning' },
          { label: 'With an agent', to: '/inbox?view=human_handling', tone: 'qualified' },
          { label: 'Unassigned', to: '/inbox?view=unassigned', tone: 'hot' },
          { label: 'Mine', to: '/inbox?view=assigned_to_me', tone: 'success' },
        ],
      },
      {
        label: 'Tickets',
        path: '/tickets',
        permission: P.conversationsView,
        tone: 'warning',
        children: [
          { label: 'All tickets', to: '/tickets', tone: 'warning' },
          { label: 'Open', to: '/tickets?view=open', tone: 'info' },
          { label: 'Waiting on customer', to: '/tickets?view=waiting', tone: 'cold' },
          { label: 'Breached SLA', to: '/tickets?view=breached', tone: 'danger' },
          { label: 'Urgent', to: '/tickets?view=urgent', tone: 'hot' },
          { label: 'Unassigned', to: '/tickets?view=unassigned', tone: 'qualified' },
        ],
        quickAction: { label: 'New ticket', flag: 'new' },
      },
      {
        label: 'Live Visitors',
        path: '/visitors',
        permission: P.conversationsView,
        tone: 'success',
      },
    ],
  },
  {
    label: 'CRM',
    tone: 'hot',
    items: [
      {
        label: 'Leads',
        path: '/leads',
        permission: P.leadsView,
        badge: 'hotLeads',
        tone: 'hot',
        children: [
          { label: 'Open', to: '/leads', tone: 'info' },
          { label: 'Follow up', to: '/leads?view=needs_followup', tone: 'warning' },
          { label: 'Hot', to: '/leads?view=hot', tone: 'hot' },
          { label: 'Qualified', to: '/leads?view=qualified', tone: 'qualified' },
          { label: 'Unassigned', to: '/leads?view=unassigned', tone: 'cold' },
          { label: 'Won', to: '/leads?view=won', tone: 'success' },
          { label: 'Lost', to: '/leads?view=lost', tone: 'danger' },
          { label: 'All leads', to: '/leads?view=all', tone: 'action' },
        ],
      },
      { label: 'Pipeline', path: '/pipeline', permission: P.dealsView, tone: 'qualified' },
    ],
  },
  {
    label: 'AI studio',
    tone: 'warning',
    items: [
      {
        label: 'Chatbots',
        path: '/bots',
        permission: P.chatbotsView,
        tone: 'action',
        quickAction: { label: 'New chatbot', flag: 'new', permission: P.chatbotsManage },
      },
      {
        label: 'Knowledge',
        path: '/knowledge',
        permission: P.knowledgeView,
        tone: 'cold',
        children: [
          { label: 'Sources', to: '/knowledge', tone: 'cold' },
          { label: 'Answer gaps', to: '/knowledge?tab=gaps', tone: 'warning' },
        ],
        quickAction: { label: 'Add source', flag: 'new', permission: P.knowledgeManage },
      },
      {
        label: 'Automations',
        path: '/automations',
        permission: P.automationsView,
        tone: 'warning',
        children: [
          { label: 'Workflows', to: '/automations', tone: 'warning' },
          { label: 'Builder', to: '/automations?tab=builder', tone: 'qualified' },
        ],
      },
    ],
  },
  {
    label: 'Commerce',
    requiresCommerce: true,
    tone: 'success',
    items: [
      { label: 'Products', path: '/products', permission: P.chatbotsView, tone: 'qualified' },
      { label: 'Orders', path: '/orders', permission: P.customersView, tone: 'info' },
      { label: 'Bookings', path: '/bookings', permission: P.customersView, tone: 'success' },
    ],
  },
  {
    label: 'Manage',
    tone: 'info',
    items: [
      {
        label: 'Analytics',
        path: '/analytics',
        permission: P.analyticsView,
        tone: 'info',
        children: [
          { label: 'Overview', to: '/analytics', tone: 'info' },
          { label: 'Team performance', to: '/analytics?tab=team', tone: 'success', permission: P.analyticsViewTeam },
          { label: 'Reports', to: '/analytics?tab=reports', tone: 'qualified' },
        ],
      },
      { label: 'Integrations', path: '/integrations', permission: P.integrationsView, tone: 'cold' },
      {
        label: 'Developers',
        path: '/developers',
        permission: P.apiKeysView,
        tone: 'action',
        children: [
          { label: 'API keys', to: '/developers', tone: 'action' },
          { label: 'Webhooks', to: '/developers?tab=webhooks', tone: 'qualified' },
          { label: 'Logs', to: '/developers?tab=logs', tone: 'cold' },
          { label: 'Docs', to: '/developers?tab=docs', tone: 'info' },
          { label: 'Test console', to: '/developers?tab=console', tone: 'success' },
        ],
      },
      {
        label: 'Team',
        path: '/team',
        permission: P.teamView,
        tone: 'success',
        children: [
          { label: 'Members', to: '/team', tone: 'success' },
          { label: 'Departments', to: '/team/departments', tone: 'info' },
          { label: 'Routing rules', to: '/team/departments?tab=routing', tone: 'qualified' },
        ],
      },
      {
        label: 'Billing',
        path: '/billing',
        permission: P.billingView,
        tone: 'warm',
        children: [
          { label: 'Overview', to: '/billing', tone: 'warm' },
          { label: 'Plans', to: '/billing?tab=plans', tone: 'action' },
          { label: 'Add-ons', to: '/billing?tab=addons', tone: 'qualified' },
          { label: 'Invoices', to: '/billing?tab=invoices', tone: 'info' },
        ],
      },
      {
        label: 'Help & support',
        path: '/support',
        badge: 'supportUnread',
        tone: 'info',
        children: [
          { label: 'My tickets', to: '/support', tone: 'info' },
          { label: 'New ticket', to: '/support/new', tone: 'action' },
        ],
      },
    ],
  },
]

/**
 * The platform panel's own navigation.
 *
 * /admin is NOT workspace-scoped, so it cannot borrow the sidebar above — the
 * links would all be relative to a workspace the admin may not be in. Without
 * this the panel had no navigation at all: you arrived by typing the URL and
 * left the same way.
 *
 * Paths here are absolute.
 */
export const PLATFORM_NAVIGATION: NavSection[] = [
  {
    label: 'Platform',
    tone: 'danger',
    items: [
      {
        label: 'Super admin',
        path: '/admin',
        tone: 'danger',
        children: [
          { label: 'Overview', to: '/admin', tone: 'danger' },
          { label: 'Tenants', to: '/admin?tab=workspaces', tone: 'action' },
          { label: 'Users', to: '/admin?tab=users', tone: 'info' },
          { label: 'Routing & budgets', to: '/admin?tab=simulator', tone: 'qualified' },
          { label: 'Costs & margins', to: '/admin?tab=costs', tone: 'warm' },
          { label: 'Plans & billing', to: '/admin?tab=billing', tone: 'success' },
          { label: 'Resellers', to: '/admin?tab=resellers', tone: 'qualified' },
          { label: 'Support tickets', to: '/admin?tab=support', tone: 'info' },
          { label: 'Feature flags', to: '/admin?tab=flags', tone: 'cold' },
          { label: 'Abuse moderation', to: '/admin?tab=abuse', tone: 'hot' },
          { label: 'Background jobs', to: '/admin?tab=jobs', tone: 'warning' },
          { label: 'Global settings', to: '/admin?tab=settings', tone: 'info' },
          { label: 'Audit logs', to: '/admin?tab=audit', tone: 'cold' },
        ],
      },
      {
        label: 'AI configuration',
        path: '/admin/ai',
        tone: 'qualified',
        children: [
          { label: 'Routing', to: '/admin/ai', tone: 'qualified' },
          { label: 'Providers', to: '/admin/ai?tab=providers', tone: 'action' },
          { label: 'Models', to: '/admin/ai?tab=models', tone: 'info' },
        ],
      },
    ],
  },
]

/**
 * The reseller panel's navigation. Like the platform panel it is not
 * workspace-scoped — a reseller is not a member of its clients' workspaces —
 * so its paths are absolute.
 */
export const RESELLER_NAVIGATION: NavSection[] = [
  {
    label: 'Reseller',
    tone: 'qualified',
    items: [
      {
        label: 'Reseller panel',
        path: '/reseller',
        tone: 'qualified',
        children: [
          { label: 'Overview', to: '/reseller', tone: 'qualified' },
          { label: 'Client companies', to: '/reseller?tab=clients', tone: 'action' },
          { label: 'Wallet', to: '/reseller?tab=wallet', tone: 'success' },
          { label: 'Upgrade requests', to: '/reseller?tab=requests', tone: 'warning' },
          { label: 'Alerts', to: '/reseller?tab=alerts', tone: 'hot' },
          { label: 'Support', to: '/reseller/support', tone: 'info' },
        ],
      },
    ],
  },
]

/** The four things the agent persona opens on a phone. */
export const MOBILE_TABS: NavItem[] = [
  { label: 'My Day', path: '/my-day', badge: 'tasksDue', tone: 'warm' },
  { label: 'Inbox', path: '/inbox', permission: P.conversationsView, badge: 'unread', tone: 'info' },
  { label: 'Leads', path: '/leads', permission: P.leadsView, tone: 'hot' },
  { label: 'Pipeline', path: '/pipeline', permission: P.dealsView, tone: 'qualified' },
]

/** Flattened for the command palette and the topbar breadcrumb. */
export function flattenNavigation(
  sections: NavSection[],
): { label: string; to: string; parent?: string; permission?: Permission; tone?: NavTone; icon: string }[] {
  const out: {
    label: string
    to: string
    parent?: string
    permission?: Permission
    tone?: NavTone
    icon: string
  }[] = []
  for (const section of sections) {
    for (const item of section.items) {
      out.push({
        label: item.label,
        to: item.path,
        ...(item.permission ? { permission: item.permission } : {}),
        ...(item.tone ? { tone: item.tone } : {}),
        icon: item.path,
      })
      for (const child of item.children ?? []) {
        out.push({
          label: child.label,
          to: child.to,
          parent: item.label,
          ...(child.permission ?? item.permission
            ? { permission: (child.permission ?? item.permission) as Permission }
            : {}),
          ...(child.tone ? { tone: child.tone } : {}),
          icon: item.path,
        })
      }
    }
  }
  return out
}
